import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useCreateRfp, useUsers, useAccounts, useRfpTags } from '@/hooks/use-api'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useDebounce } from '@/hooks/use-debounce'
import { useUser } from '@/contexts/UserContext'
import { useNavigate } from 'react-router-dom'
import type { Rfp, RfpStage } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateRfpModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId?: string
}

const STAGES: RfpStage[] = [
  'Qualify',
  'In Progress',
  'Submitted',
  'Shortlisted',
  'Negotiations',
  'Won',
  'Lost',
]

export function CreateRfpModal({ open, onOpenChange, accountId }: CreateRfpModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const navigate = useNavigate()
  const createMutation = useCreateRfp()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: tagsData } = useRfpTags()
  const [accountSearch, setAccountSearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const { data: accountsData, isFetching: accountsLoading } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })

  const users = usersData?.data || []
  const accounts = accountsData?.data || []
  const tags = tagsData || []

  const [formData, setFormData] = useState<Partial<Rfp> & { tag_ids?: string[] }>({
    name: '',
    bid_number: '',
    stage: 'Qualify',
    account_id: '',
    account_manager_id: currentUser?.id || '',
    client_name: '',
    rfp_url: '',
    issue_date: '',
    submission_deadline: '',
    award_date: '',
    estimated_value: undefined,
    contract_term: undefined,
    google_drive_url: '',
    description: '',
    tag_ids: [],
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-fill account when opened from account detail
  useEffect(() => {
    if (open && accountId) {
      setFormData((prev) => ({ ...prev, account_id: accountId }))
    }
  }, [open, accountId])

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field])
      setErrors((prev) => {
        const n = { ...prev }
        delete n[field]
        return n
      })
  }

  const toggleTag = (tagId: string) => {
    setFormData((prev) => {
      const current = prev.tag_ids || []
      return {
        ...prev,
        tag_ids: current.includes(tagId)
          ? current.filter((id) => id !== tagId)
          : [...current, tagId],
      }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!formData.name?.trim()) newErrors.name = 'RFP name is required'
    if (!formData.account_manager_id) newErrors.account_manager_id = 'Account manager is required'
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      const result = await createMutation.mutateAsync(formData as any)
      toast({ title: 'Success', description: 'RFP created successfully.' })
      setFormData({
        name: '',
        bid_number: '',
        stage: 'Qualify',
        account_id: '',
        account_manager_id: currentUser?.id || '',
        client_name: '',
        rfp_url: '',
        issue_date: '',
        submission_deadline: '',
        award_date: '',
        estimated_value: undefined,
        google_drive_url: '',
        description: '',
        tag_ids: [],
      })
      onOpenChange(false)
      if (result?.id) navigate(`/rfps/${result.id}`)
    } catch (error) {
      // Surface the server's reason instead of a generic string — a hardcoded
      // "Failed to create RFP." is why a total create outage looked like an
      // unexplained glitch and needed prod logs to diagnose.
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to create RFP'),
        variant: 'destructive',
      })
    }
  }

  const selectClassName =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-rfp-desc"
      >
        <DialogHeader>
          <DialogTitle>Create New RFP</DialogTitle>
          <DialogDescription id="create-rfp-desc">
            Fill in the details for the new RFP. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="rfp-name">RFP Name *</Label>
              <Input
                id="rfp-name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter RFP name"
                aria-required="true"
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1" role="alert">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="bid-number">Bid Number</Label>
              <Input
                id="bid-number"
                value={formData.bid_number}
                onChange={(e) => handleChange('bid_number', e.target.value)}
                placeholder="e.g. BID-2026-042"
              />
            </div>

            <div>
              <Label htmlFor="rfp-stage">Stage</Label>
              <select
                id="rfp-stage"
                value={formData.stage}
                onChange={(e) => handleChange('stage', e.target.value)}
                className={selectClassName}
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Account</Label>
              <RecordCombobox
                id="rfp-account"
                records={accounts}
                value={formData.account_id as string}
                onValueChange={(v) => handleChange('account_id', v)}
                placeholder="Select account..."
                searchPlaceholder="Search accounts..."
                emptyMessage="No accounts found."
                displayField={(a) => a.name || ''}
                onSearchChange={setAccountSearch}
                isLoading={accountsLoading}
                aria-label="Account"
                totalCount={accountsData?.total}
              />
            </div>

            <div>
              <Label htmlFor="rfp-manager">Account Manager *</Label>
              <select
                id="rfp-manager"
                value={formData.account_manager_id}
                onChange={(e) => handleChange('account_manager_id', e.target.value)}
                className={selectClassName}
                aria-required="true"
              >
                <option value="">Select manager...</option>
                {users
                  .filter(
                    (u) =>
                      ['User', 'Admin', 'Super Admin'].includes(u.user_type) &&
                      u.is_active !== false &&
                      u.email?.includes('@grackledocs.com')
                  )
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
              {errors.account_manager_id && (
                <p className="text-sm text-destructive mt-1" role="alert">
                  {errors.account_manager_id}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="rfp-url">RFP URL</Label>
              <Input
                id="rfp-url"
                value={formData.rfp_url}
                onChange={(e) => handleChange('rfp_url', e.target.value)}
                placeholder="https://..."
                type="url"
              />
            </div>

            <div>
              <Label htmlFor="rfp-value">Estimated Value</Label>
              <Input
                id="rfp-value"
                value={formData.estimated_value ?? ''}
                onChange={(e) =>
                  handleChange(
                    'estimated_value',
                    e.target.value ? parseFloat(e.target.value) : undefined
                  )
                }
                placeholder="0.00"
                type="number"
                step="0.01"
              />
            </div>

            <div>
              <Label htmlFor="rfp-contract-term">Contract Term</Label>
              <select
                id="rfp-contract-term"
                value={formData.contract_term ?? ''}
                onChange={(e) =>
                  handleChange(
                    'contract_term',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className={selectClassName}
                aria-label="Contract term"
              >
                <option value="">Select...</option>
                <option value="12">1 Year</option>
                <option value="24">2 Years</option>
                <option value="36">3 Years</option>
                <option value="48">4 Years</option>
                <option value="60">5 Years</option>
                <option value="72">5+ Years</option>
              </select>
            </div>

            <div>
              <Label htmlFor="rfp-issue-date">Issue Date</Label>
              <DatePicker
                id="rfp-issue-date"
                value={formData.issue_date}
                onChange={(v) => handleChange('issue_date', v ?? '')}
                aria-label="Issue Date"
              />
            </div>

            <div>
              <Label htmlFor="rfp-submission">Submission Deadline</Label>
              <DatePicker
                id="rfp-submission"
                value={formData.submission_deadline}
                onChange={(v) => handleChange('submission_deadline', v ?? '')}
                aria-label="Submission Deadline"
              />
            </div>

            <div>
              <Label htmlFor="rfp-award">Award Date</Label>
              <DatePicker
                id="rfp-award"
                value={formData.award_date}
                onChange={(v) => handleChange('award_date', v ?? '')}
                aria-label="Award Date"
              />
            </div>

            <div>
              <Label htmlFor="rfp-gdrive">Google Drive URL</Label>
              <Input
                id="rfp-gdrive"
                value={formData.google_drive_url}
                onChange={(e) => handleChange('google_drive_url', e.target.value)}
                placeholder="https://drive.google.com/..."
                type="url"
              />
            </div>

            {/* Tags */}
            <div className="col-span-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      formData.tag_ids?.includes(tag.id)
                        ? 'text-white border-transparent'
                        : 'text-foreground border-border bg-background hover:bg-muted'
                    }`}
                    style={
                      formData.tag_ids?.includes(tag.id)
                        ? { backgroundColor: tag.color }
                        : undefined
                    }
                    aria-pressed={formData.tag_ids?.includes(tag.id)}
                    aria-label={`Tag: ${tag.name}`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="col-span-2">
              <Label htmlFor="rfp-desc">Description / Notes</Label>
              <textarea
                id="rfp-desc"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Additional details..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create RFP'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
